import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Components
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KOGFileUploader from "@/main/components/shared/fileUploader/fileUploader";

import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { rateImportApi } from "@/main/api";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";

// Utils
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
} from "@/main/services/utils";
import { userIsInsurer } from "@/main/services/utilsUser";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { KeyValue } from "@/main/models/common";

// Icons
import { IconButton, Typography, LinearProgress, Box } from "@mui/material";
import GetApp from "@mui/icons-material/GetApp";
import Info from "@mui/icons-material/Info";

import * as S from "./styles";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { downloadFile } from "@/main/services/utilsFile";

interface BodyshopRateImportModalProps {
	isOpen: boolean;
	closeModal: () => void;
}

interface IImportBodyshopRateForm {
	country: KeyValue | null;
	insurer: KeyValue | null;
}

export function BodyshopRateImportModal({ isOpen, closeModal }: BodyshopRateImportModalProps) {
	const dispatch = useDispatch();

	const { countriesUser: countryReducer } = useCountryReducer();
	const { insurers: insurerReducer } = useInsurerReducer();

	const disabledCountry = countryReducer?.data?.length === 1;
	const disabledInsurer = userIsInsurer();

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
	const optionDefaultInsurerDrop = userIsInsurer() ? getDefaultOptionDrop(insurerReducer.data) : null;

	const INITIAL_FORM_STATE = {
		country: optionDefaultCountryDrop as KeyValue | null,
		insurer: optionDefaultInsurerDrop as KeyValue | null,
	};

	const methods = useForm<IImportBodyshopRateForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const { control, setValue, watch, handleSubmit, reset, getValues } = methods;

	const watcherCountry = watch("country");

	const [files, setFiles] = useState<File[]>([]);

	useEffect(() => {
		if (!isOpen) {
			reset(INITIAL_FORM_STATE);
			setFiles([]);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());

		return () => {
			dispatch(resetReducerInsurers());
		};
	}, []);

	useEffect(() => {
		if (isNullUndefined(watcherCountry)) {
			setValue("insurer", null);
			dispatch(resetReducerInsurers());
			return;
		}

		dispatch(
			getInsurers({
				guidCountry: String(watcherCountry.key),
				idStatusRegister: StatusRegisterEnum.Active,
			}),
		);
	}, [watcherCountry?.key]);

	useEffect(() => {
		if (countryReducer.data?.length === 1 && optionDefaultCountryDrop) {
			setValue("country", optionDefaultCountryDrop);
		}
	}, [countryReducer.data]);

	useEffect(() => {
		if (userIsInsurer() && insurerReducer.data?.length === 1 && optionDefaultInsurerDrop) {
			setValue("insurer", optionDefaultInsurerDrop);
		}
	}, [insurerReducer.data]);

	const [exporting, setExporting] = useState(false);

	const handleDownloadTemplate = () => {
		const formParams = getValues();

		if (isNullUndefined(formParams.country?.key)) {
			toast.error(getTranslateByKey("information.countryRequired", true));
			return;
		}

		setExporting(true);

		rateImportApi
			.getDownloadFileImportSample()
			.then(async (response) => {
				const result = response.data.result;
				downloadFile(result.base64, result.fileName);

				toast.success(getTranslateByKey("information.downloadTemplate"));
			})
			.catch((error) => {
				console.error("Error:", error);
			})
			.finally(() => {
				setExporting(false);
			});
	};

	const [isLoading, setIsLoading] = useState(false);

	const onSubmit = (data: IImportBodyshopRateForm) => {
		if (files.length === 0) {
			toast.error(getTranslateByKey("importInventory.pleaseSelectAtLeast1File"));
			return;
		}

		try {
			setIsLoading(true);
			const request = {
				guidCountry: data.country ? String(data.country.key) : "",
				guidInsurer: data.insurer ? String(data.insurer.key) : "",
			};

			rateImportApi.postImportRates(request, files[0]);
			toast.success(getTranslateByKey("information.fileImportIsBeingProcessed"));
			setFiles([]);
			reset(INITIAL_FORM_STATE);
			closeModal();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<S.Container>
			<S.HeaderContainer>
				{exporting ? (
					<S.ProgressContainer>
						<LinearProgress style={{ flexGrow: 1, marginRight: "8px" }} />
						<Typography variant="body1">
							{getTranslateByKey("importInventory.exporting")}
							...
						</Typography>
					</S.ProgressContainer>
				) : (
					<S.DownloadContainer>
						<button
							style={{
								display: "flex",
								alignItems: "center",
								cursor: "pointer",
								margin: "0 auto",
								justifyContent: "center",
								background: "none",
								border: "none",
								padding: 0,
							}}
							onClick={handleDownloadTemplate}
							id="id-bodyshopImport-btn-download-template"
						>
							<IconButton
								size="medium"
								edge="start"
								color="primary"
								aria-label="Download Template"
								style={{ width: 40, top: 4 }}
								component="span"
							>
								<GetApp />
							</IconButton>
							<Typography
								variant="body1"
								style={{
									fontSize: 14,
									borderBottom: "1px solid #3f5ec9",
									marginLeft: "8px",
									color: "#3f5ec9",
								}}
							>
								{getTranslateByKey("information.clickHereToDownloadSpreadsheetTemplate")}
							</Typography>
						</button>
					</S.DownloadContainer>
				)}
			</S.HeaderContainer>

			<FormProvider {...methods}>
				<S.FormContainer onSubmit={handleSubmit(onSubmit)} noValidate>
					<KogAutoCompleteControlled
						required
						id="id-bodyshopImport-select-country"
						nameCustom="country"
						control={control}
						label={getTranslateByKey("information.country")}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						isDefaultValueUnique
						disabled={disabledCountry}
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						required
						id="id-bodyshopImport-select-insurer"
						nameCustom="insurer"
						control={control}
						label={getTranslateByKey("information.insurer")}
						options={insurerReducer.data}
						isLoading={insurerReducer.isLoading}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						isDefaultValueUnique={optionDefaultInsurerDrop !== null}
						disabled={disabledInsurer || isNullUndefined(watcherCountry)}
						onChangeCustom={() => {}}
					/>

					<KOGFileUploader
						files={files}
						setFiles={setFiles}
						typeFiles={["xlsx"]}
						maxFiles={1}
						isRequired
						isFullScreenDragDrop
					/>
					<Box display="flex" justifyContent="center" width="100%">
						<ButtonSubmit files={files} uploading={isLoading} />
					</Box>

					<div>
						<S.InfoContainer>
							<Info />
							<Typography
								variant="body1"
								style={{
									wordWrap: "break-word",
									maxWidth: "90%",
									color: "gray",
									textAlign: "center",
								}}
							>
								{getTranslateByKey("importInventory.fileHasMoreThanXLines").replace("{0}", "500")}
							</Typography>
						</S.InfoContainer>
					</div>
				</S.FormContainer>
			</FormProvider>
		</S.Container>
	);
}

interface IButtonSubmitProps {
	uploading: boolean;
	files: File[];
}
const ButtonSubmit = ({ files, uploading }: IButtonSubmitProps) => {
	const { watch } = useFormContext<IImportBodyshopRateForm>();
	const formsWatch = watch();

	const validFields = [isNullUndefined(formsWatch.country?.key), isNullUndefined(formsWatch.insurer?.key)];

	const isDisabled = uploading || files.length === 0 || validFields.some((field) => field);

	return (
		<SecondaryButton type="submit" id="id-bodyshopImport-btn-import" disabled={isDisabled}>
			{uploading ? getTranslateByKey("importInventory.uploading") + "..." : getTranslateByKey("importInventory.import")}
		</SecondaryButton>
	);
};

export default BodyshopRateImportModal;
